"use strict"
var _ = require('lodash')
var inherits = require('util').inherits
var TimeSeriesRingBuffer = require('time-series-ring-buffer')

var BUFFER_SIZE = 0
var DEFAULT_NUM_POINTS = 30

var second = 1000,
  minute = second * 60,
  hour = minute * 60,
  day = hour * 24
var durations = [second, minute, hour, day]
var durationLabels = ["seconds", "minutes", "hours", "days"]

var getInterval = function (windowSize, numPoints) {
  if (!windowSize) {
    return {interval: 1000, intervalParam: "1second"}
  }
  var intervalSize = windowSize / numPoints
  var durationIndex = _.findIndex(durations, function (duration) {
    return duration > intervalSize
  })

  if (durationIndex === -1) {
    durationIndex = durations.length - 1
  } else if (durationIndex > 0) {
    durationIndex -= 1
  }

  var duration = durations[durationIndex]
  var durationMultiple = Math.floor(intervalSize / duration)
  return {
    interval: duration * durationMultiple,
    intervalParam: "" + durationMultiple + durationLabels[durationIndex]
  }
}

var validAggregate = {
  "sum": true
}

var sizes = {
  "small": 30,
  "medium": 60,
  "large": 120,
  "macro": 600
}

function CounterGraph (graphOptions) {
  if (!(this instanceof CounterGraph)) {
    return new CounterGraph(graphOptions)
  }
  var aggregate = graphOptions.aggregate || "sum"
  if (!validAggregate[aggregate]) {
    throw new Error("Invalid aggregate value: " + aggregate + ". Valid inputs: " + Object.keys(validAggregate).join(', '))
  }

  this.aggregate = aggregate
  this.counterTypes = graphOptions.counterTypes || []
  this.createDefaultHistory()
  this.lastStartPos = -1

  var size = sizes[graphOptions.size] || DEFAULT_NUM_POINTS
  this.size = size

  var numPoints = this.size + BUFFER_SIZE
  var windowSize = graphOptions.retroHistoryDuration
  var interval = getInterval(windowSize, numPoints).interval
  this.interval = interval

  //calculate interval / numPoints
  TimeSeriesRingBuffer.call(this, {
    interval: interval,
    bufferLength: numPoints
  })
}
inherits(CounterGraph, TimeSeriesRingBuffer)

var proto = CounterGraph.prototype

proto.resetHistory = function (options) {
  this.interval = options.interval
  this.isLive = options.isLive
  if (this.isLive) {
    this.bufferLength = this.size + BUFFER_SIZE
  } else {
    this.bufferLength = options.numPoints
  }
  this.lastStartPos = -1
  this.initBuffer()
}

proto.createDefaultHistory = function () {
  var dataPoint = {timestamp: 0}
  this.defaultHistory = [dataPoint, dataPoint]
}

proto._createDefaultBufferData = function () {
  var counterTypes = this.counterTypes
  var data = {
    timestamp: 0,
    value: 0,
    values: {}
  }
  var i
  for (i = 0; i < counterTypes.length; i += 1) {
    data.values[counterTypes[i]] = {
      value: 0,
      latestObservedTime: 0
    }
  }
  return data
}

proto._resetBufferData = function (bufferData) {
  var counterTypes = this.counterTypes
  var counterTypesLen = counterTypes.length
  bufferData.value = 0
  var values = bufferData.values
  var i
  for (i = 0; i < counterTypesLen; i += 1) {
    values[counterTypes[i]].value = 0
    values[counterTypes[i]].latestObservedTime = 0
  }
}

proto._mergeData = function (bufferData, data) {
  if (!bufferData) { return }
  var counterTypes = this.counterTypes
  var len = counterTypes.length
  var i, bufferTypeData, dataTypeData
  bufferData.value = data.value || 0
  for (i = 0; i < len; i += 1) {
    dataTypeData = data.values[counterTypes[i]]
    bufferTypeData = bufferData.values[counterTypes[i]]
    if (dataTypeData != null) {
      if (dataTypeData.value != null) {
        bufferTypeData.value = dataTypeData.value
      }
      if (dataTypeData.latestObservedTime != null && dataTypeData.latestObservedTime > bufferTypeData.latestObservedTime) {
        bufferTypeData.latestObservedTime = dataTypeData.latestObservedTime
      }
    }
  }
}

proto.getNextStartingPos = function () {
  var maxPos = this.bufferPos - this.size + 1
  if (this.lastStartPos < 0) {
    return maxPos - Math.floor(BUFFER_SIZE / 2)
  }
  var nextPos = this.lastStartPos + 1
  if (nextPos > maxPos) { return maxPos }
  var minPos = maxPos - BUFFER_SIZE
  if (nextPos < minPos) { return minPos }
  return nextPos
}

var transitionDurations = [
  1800, //0
  1500, //1
  1300, //2
  1100, //3
  1000, //4
  1000, //5
  900, //6
  800, //7
  700, //8
  500, //9
]

proto.getTransitionDuration = function () {
  var maxPos = this.bufferPos - this.size + 1
  var ammountBuffered = maxPos - this.lastStartPos
  if (ammountBuffered < 0) { return transitionDurations[0]}
  if (ammountBuffered > 9) { return transitionDurations[9]}
  return transitionDurations[ammountBuffered]
}

proto.getHistory = function () {
  var history = []
  this.fromPositionForNumPoints(this.getNextStartingPos(), this.size, function (bufferData) {
    history.push(bufferData)
  })
  return history
}

proto.getGraphData = function () {
  var self = this
  var history = this.getHistory()
  if (!history.length) {
    history = this.defaultHistory
  } else {
    this.lastStartPos = this.getBufferPosFromTimestamp(history[0].timestamp)
  }
  var endTime = Date.now()
  var startTime = endTime - this.interval * this.size
  var transitionType = 'play'

  var maxValue = _.max(history, 'value')
  maxValue = (maxValue && maxValue.value) ? maxValue.value : 0

  var transitionDuration = this.getTransitionDuration()

  var values = {
    "retroLowObs": [],
    "retroMediumObs": [],
    "retroHighObs": []
  }

  _.each(history, function (dataPoint) {
    if (!dataPoint.value) { return }
    _.each(dataPoint.values, function (valueInfo, counterType) {
      if (!valueInfo.value || !values[counterType]) { return }
      var nextDataPoint = {
        count: valueInfo.value,
        startTime: dataPoint.timestamp,
        endTime: dataPoint.timestamp + self.interval - 1,
        latestObservedTime: valueInfo.latestObservedTime
      }
      values[counterType].push(nextDataPoint)
    })
  })

  return {
    low: values.retroLowObs,
    medium: values.retroMediumObs,
    high: values.retroHighObs,
    transitionDuration: transitionDuration,
    transitionType: transitionType,
    startTime: startTime,
    endTime: endTime,
    interval: this.interval,
    maxValue: maxValue
  }
}

proto.destroy = function () {
  if (this.destroyed) { return }
  this.destroyed = true
}

module.exports = CounterGraph
